/**
 * IndexedDB data cache for offline access.
 * Stores events, tasks, notes, and a sync queue so the PWA can display data
 * and replay mutations when offline.
 * Uses the 'idb' library for a Promise-based wrapper.
 */

import { openDB, type IDBPDatabase } from 'idb';
import type { Event, Task, Note } from '@anna-calendar/shared';

const DB_NAME = 'annacalendar-cache';
const DB_VERSION = 2;

/** A queued mutation waiting to be replayed on reconnect. */
export interface SyncQueueEntry {
  id: number;
  method: 'POST' | 'PUT' | 'DELETE';
  path: string;
  body: string | null;
  createdAt: string;
}

interface CalendarDB {
  events: { key: string; value: Event; indexes: { 'by-updated': string } };
  tasks: { key: string; value: Task; indexes: { 'by-updated': string } };
  notes: { key: string; value: Note; indexes: { 'by-date': string; 'by-updated': string } };
  syncQueue: { key: number; value: SyncQueueEntry; indexes: Record<string, never> };
}

let dbPromise: Promise<IDBPDatabase<CalendarDB>> | null = null;

function getDB(): Promise<IDBPDatabase<CalendarDB>> {
  if (!dbPromise) {
    dbPromise = openDB<CalendarDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // v1: create entity stores
        if (oldVersion < 1) {
          const eventsStore = db.createObjectStore('events', { keyPath: 'id' });
          eventsStore.createIndex('by-updated', 'updated_at');

          const tasksStore = db.createObjectStore('tasks', { keyPath: 'id' });
          tasksStore.createIndex('by-updated', 'updated_at');

          const notesStore = db.createObjectStore('notes', { keyPath: 'id' });
          notesStore.createIndex('by-date', 'date');
          notesStore.createIndex('by-updated', 'updated_at');
        }

        // v2: add sync queue for offline mutations
        if (oldVersion < 2) {
          db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        }
      },
    });
  }
  return dbPromise;
}

// ─── Events ────────────────────────────────────────────────────────────────

export async function cacheEvents(events: Event[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('events', 'readwrite');
  await Promise.all(events.map((e) => tx.store.put(e)));
  await tx.done;
}

export async function getCachedEvents(params?: { start?: string; end?: string }): Promise<Event[]> {
  const db = await getDB();
  const all = await db.getAll('events');
  if (!params?.start && !params?.end) return all;
  return all.filter((e) => {
    if (params?.start && e.start_time < params.start) return false;
    if (params?.end && e.start_time >= params.end) return false;
    return true;
  });
}

export async function cacheEvent(event: Event): Promise<void> {
  const db = await getDB();
  await db.put('events', event);
}

export async function removeCachedEvent(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('events', id);
}

// ─── Tasks ─────────────────────────────────────────────────────────────────

export async function cacheTasks(tasks: Task[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('tasks', 'readwrite');
  await Promise.all(tasks.map((t) => tx.store.put(t)));
  await tx.done;
}

export async function getCachedTasks(params?: {
  due_date?: string;
  due_date_start?: string;
  due_date_end?: string;
  priority?: number;
}): Promise<Task[]> {
  const db = await getDB();
  const all = await db.getAll('tasks');
  if (!params?.due_date && !params?.due_date_start && !params?.due_date_end && params?.priority === undefined) return all;
  return all.filter((t) => {
    if (params?.due_date && t.due_date !== params.due_date) return false;
    if (params?.due_date_start && (!t.due_date || t.due_date < params.due_date_start)) return false;
    if (params?.due_date_end && (!t.due_date || t.due_date >= params.due_date_end)) return false;
    if (params?.priority !== undefined && t.priority !== params.priority) return false;
    return true;
  });
}

export async function cacheTask(task: Task): Promise<void> {
  const db = await getDB();
  await db.put('tasks', task);
}

export async function removeCachedTask(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('tasks', id);
}

// ─── Notes ─────────────────────────────────────────────────────────────────

export async function cacheNotes(notes: Note[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('notes', 'readwrite');
  await Promise.all(notes.map((n) => tx.store.put(n)));
  await tx.done;
}

export async function getCachedNotes(params?: { date?: string }): Promise<Note[]> {
  const db = await getDB();
  if (params?.date) {
    return db.getAllFromIndex('notes', 'by-date', params.date);
  }
  return db.getAll('notes');
}

export async function cacheNote(note: Note): Promise<void> {
  const db = await getDB();
  await db.put('notes', note);
}

export async function removeCachedNote(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('notes', id);
}

// ─── Sync Queue (offline mutations) ────────────────────────────────────────

/** Add a mutation to the sync queue for later replay. */
export async function enqueueMutation(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown | null,
): Promise<void> {
  const db = await getDB();
  await db.put('syncQueue', {
    id: 0, // autoIncrement ignores this
    method,
    path,
    body: body ? JSON.stringify(body) : null,
    createdAt: new Date().toISOString(),
  });
}

/** Retrieve all queued mutations in FIFO order. */
export async function getQueuedMutations(): Promise<SyncQueueEntry[]> {
  const db = await getDB();
  return db.getAll('syncQueue');
}

/** Remove a single queued mutation by id after successful replay. */
export async function dequeueMutation(id: number): Promise<void> {
  const db = await getDB();
  await db.delete('syncQueue', id);
}

/** Clear the entire sync queue (e.g., after all replayed successfully). */
export async function clearSyncQueue(): Promise<void> {
  const db = await getDB();
  await db.clear('syncQueue');
}
